import { selectors } from 'modules/common';
import { useSelector } from 'modules/react-redux';
import { NetSuiteSalesTaxItem } from 'pages/requestForm/data/netsuite/NetSuiteContext';
import { useGetRecognizedOcrAttachment } from 'pages/requestForm/hooks/useGetRecognizedOcrAttachment';
import { useOcrData } from 'pages/requestForm/hooks/useOcrData';
import { requestSelector } from 'pages/requestForm/selectors/pageSelectors';
import { getRequestEditMode } from 'pages/requestForm/selectors/requestSelectors';
import { getRuleTypeByEditMode } from 'pages/requestForm/utils/helpers';
import { useEffect, useMemo, useState } from 'react';
import { useGetNetSuiteTaxCodes } from 'shared/data/webApp/v1';
import { getSelectorSuggestedItems, isOcrEnabledForTemplate } from 'shared/helpers';
import { useCompanyBetaFeatures } from 'shared/hooks';

const useOcrTaxCodes = (externalTaxCodes: NetSuiteSalesTaxItem[], lineIndex?: number) => {
    const request = useSelector(requestSelector);
    const company = useSelector((state) => selectors.company.getCompanyById(state, request.companyId));
    const ocrAttachment = useGetRecognizedOcrAttachment(request);
    const shouldUseOcr = ocrAttachment && isOcrEnabledForTemplate(company, request.integrationCode);

    const {
        data: ocrData,
        refetch: refetchOcrData,
        isFetching: isOcrDataFetching,
    } = useOcrData(request, ocrAttachment?.id);

    useEffect(() => {
        if (shouldUseOcr && !ocrData) refetchOcrData();
    }, [shouldUseOcr, ocrData, refetchOcrData]);

    const taxCodes = useMemo(() => {
        if (!shouldUseOcr || !ocrData || !lineIndex) return externalTaxCodes;

        const line = ocrData?.document?.lineItems?.[lineIndex];
        const ocrTaxCodeIds = (line && 'taxCodeIds' in line && line.taxCodeIds) || [];

        return getSelectorSuggestedItems(externalTaxCodes, ocrTaxCodeIds);
    }, [externalTaxCodes, shouldUseOcr, ocrData, lineIndex]);

    return {
        taxCodes,
        isOcrDataFetching,
    };
};

export const useTaxCodes = (query?: string, lineIndex?: number) => {
    const request = useSelector(requestSelector);
    const editMode = useSelector((state) => getRequestEditMode(state, request));
    const ruleType = getRuleTypeByEditMode(editMode);
    const { id: requestId, companyId } = request;
    const { isNetSuiteShowAllTaxes } = useCompanyBetaFeatures(companyId);

    const [currentTaxCodes, setCurrentTaxCodes] = useState<NetSuiteSalesTaxItem[]>([]);

    const { data: netSuiteTaxCodes, isFetching: isFetchingNetSuiteTaxCodes } = useGetNetSuiteTaxCodes(
        {
            query: {
                requestId,
                ruleType,
                query,
                startFrom: 0,
                rowNum: isNetSuiteShowAllTaxes ? 20000 : 20,
                companyId,
            },
        },
        {
            staleTime: 1000 * 60 * 5,
        }
    );

    useEffect(() => {
        if (!netSuiteTaxCodes?.values) return;

        setCurrentTaxCodes(
            netSuiteTaxCodes.values.map((taxCode) => ({
                ...taxCode,
                text: taxCode.name,
                subName: '',
            }))
        );
    }, [netSuiteTaxCodes]);

    const { taxCodes, isOcrDataFetching } = useOcrTaxCodes(currentTaxCodes, lineIndex);

    return {
        taxCodes,
        isLoading: isFetchingNetSuiteTaxCodes || isOcrDataFetching,
    };
};
